/* eslint-disable no-param-reassign */
import { createActionTypeReducer } from 'util/reducers'
import { without, withUnshift, withPush } from 'util/arrays'
import {
  normalizeSearchQueryId,
  constructGraphQLFilterObject,
  constructGraphQLOrderByObject,
  removeContextFromQueryId,
  isForCurrentUser,
  removeKeysFromQueryId,
} from 'ducks/searches/utils/query'
import debug from 'util/debug'
import { deepCopy } from 'util/objects'
import { byOldest } from 'util/search/sorting'
import {
  ROOM_OPTIMISTIC_UPDATED,
  ROOMS_UPDATED,
  ROOM_DELETE_STARTED,
} from 'ducks/chat/actionTypes/rooms'

import { getRoomId } from 'ducks/chat/utils/rooms'

import {
  SEARCH_CONVERSATIONS_STARTED,
  SEARCH_CONVERSATIONS_SUCCESS,
  SEARCH_CONVERSATIONS_FAILED,
  FETCH_CONVERSATION_COUNTS_FOR_CHANNEL_SUCCESS,
  SEARCH_SYNC,
} from '../actionTypes'
import { calculateBasicDiff } from '../utils/diff'

const byQueryIdInitialState = {}

function createDefaultQuery(queryId) {
  const orderBy = constructGraphQLOrderByObject(queryId) || {}
  const defaultQ = {
    queryId,
    conversationIds: [],
    removedConversationIds: [],
    addedConversationIds: [],
    // highlights: {},
    resultsIncomplete: false,
    filter: constructGraphQLFilterObject(queryId),
    orderBy,
    resultsStale: false,
    cursors: [],
    currentPageCursor: null,
    nextPageCursor: null,
    loading: false,
    loaded: false,
    // so the value here is a placeholder
  }
  return defaultQ
}

function storeQueryConversationCount({
  queryType,
  channelId,
  draftState,
  countKey = 'conversationCount',
}) {
  return ({ id, [countKey]: conversationCount }) => {
    let queryId = `type:widget ${queryType}${id}`
    if (channelId) {
      queryId = `channel:${channelId} ${queryId}`
    }
    queryId = normalizeSearchQueryId(queryId)
    if (!draftState[queryId]) {
      draftState[queryId] = createDefaultQuery(queryId)
    }
    Object.assign(draftState[queryId], {
      conversationCount,
    })
  }
}

const reducers = {}

reducers[FETCH_CONVERSATION_COUNTS_FOR_CHANNEL_SUCCESS] = (
  draftState,
  {
    payload: {
      data: { node },
    },
    meta: {
      requestParameters: { channelId },
    },
  }
) => {
  const {
    folders: { nodes: folderCounts },
    tags: { nodes: tagCounts },
    permittedAgents: { nodes: agentCounts },
  } = node

  folderCounts.forEach(
    storeQueryConversationCount({
      queryType: 'folder:',
      draftState,
      channelId,
    })
  )

  folderCounts.forEach(
    storeQueryConversationCount({
      queryType: 'folderunread:',
      draftState,
      channelId,
      countKey: 'unreadCount',
    })
  )

  tagCounts.forEach(
    storeQueryConversationCount({
      queryType: 'tag:',
      draftState,
      channelId,
    })
  )

  agentCounts.forEach(
    storeQueryConversationCount({
      queryType: 'agent:',
      draftState,
      channelId,
    })
  )

  return draftState
}

reducers[SEARCH_CONVERSATIONS_STARTED] = (
  draftState,
  {
    meta: {
      requestParameters: { queryId },
    },
  }
) => {
  if (!draftState[queryId]) {
    draftState[queryId] = createDefaultQuery(queryId)
  }
  Object.assign(draftState[queryId], {
    loading: true,
    errored: false,
  })
  return draftState
}

reducers[SEARCH_CONVERSATIONS_FAILED] = (
  draftState,
  {
    payload: { error },
    meta: {
      requestParameters: { queryId },
    },
  }
) => {
  if (!draftState[queryId]) {
    draftState[queryId] = createDefaultQuery(queryId)
  }
  Object.assign(draftState[queryId], {
    loading: false,
    errored: true,
    error,
  })
  return draftState
}

reducers[SEARCH_CONVERSATIONS_SUCCESS] = (
  draftState,
  {
    payload: {
      rooms: {
        nodes,
        pageInfo: { endCursor, hasNextPage },
        totalCount,
        totalPageCount,
      },
    },
    meta: {
      requestParameters: { queryId, cursor, orderBy },
    },
  }
) => {
  if (!draftState[queryId]) {
    draftState[queryId] = createDefaultQuery(queryId)
  }
  // The first page doesnt have a cursor, and so its value would be null
  const searchCursor = cursor || null
  const conversationIds = nodes.map(({ id }) => id)
  const searchConversationIds = draftState[queryId].conversationIds
    // The order from elasticsearch is king. If the conversation id has been retured
    // from the backend, then move the conversationId to its new position in the list
    .filter(conversationId => !conversationIds.includes(conversationId))
    // Newely loaded ids always goes into the bottom of the array
    .concat(conversationIds)

  // const areResultsStale = !!draftState[queryId].resultsStale
  // const highlights = nodes.reduce((result, conversation) => {
  //   // TODO: In the old system this was called search_summary. This needs
  //   // to be renamed to match our naming convention
  //   if (conversation.searchSummary)
  //     result[conversation.id] = conversation.searchSummary
  //   return result
  // }, {})

  let conversationCount = totalCount
  // When update conversations, there timestamps get updated which means they bubble to the top
  // of the conversation list. This means that when we load the first page in the default order
  // we can use the results to check if the recent changes has been indexed and clear the
  // addedConversations array which keeps track of local changes
  if (searchCursor === null && orderBy === null) {
    const { addedConversationIds, removedConversationIds } = draftState[queryId]
    // When we optimistically move conversations around we add them to addedConversationIds
    // and removedConversationIds, this means that if the customer quickly navigates to
    // the new folder before ES has been updated, we'll add/remove the conversation in the
    // list even if the latest respond from ES differs.
    conversationIds.forEach(conversationId => {
      without(addedConversationIds, conversationId)
      if (removedConversationIds.includes(conversationId)) {
        without(searchConversationIds, conversationId)
        conversationCount -= 1
      }
    })
    conversationCount += addedConversationIds.length
  }

  Object.assign(draftState[queryId], {
    queryId,
    conversationCount,
    currentPageCursor: searchCursor,
    nextPageCursor: endCursor,
    totalPages: totalPageCount,
    orderBy,
    loading: false,
    loaded: true,
    errored: false,
    resultsStale: false,
    resultsIncomplete: false,
    // highlights,
    hasAllPages: draftState[queryId].hasAllPages || !hasNextPage,
    conversationIds: searchConversationIds,
    cursors: draftState[queryId].cursors.concat([searchCursor]),
  })
  return draftState
}

reducers[SEARCH_SYNC] = (
  draftState,
  {
    payload: {
      currentLastUpdatedAt,
      lastUpdatedAt,
      searches,
      currentUser: { id: currentUserId },
    },
  }
) => {
  // We can only start syncing counts after the initial counts has been loaded via
  // FETCH_CONVERSATION_COUNTS_FOR_CHANNEL
  if (!currentLastUpdatedAt) return
  // We're using ephemeral events in matrix which will automatically remove the
  // json content after the specified period (currently its 1 minute after creation)
  // The event still existing, but there is no content. If we recieve an event like
  // this we should just ignore it
  if (!lastUpdatedAt || !searches) return
  // If the data in our store is up to date or newer that the data getting loaded, then
  // just ignore this update
  if (currentLastUpdatedAt >= lastUpdatedAt) return

  Object.keys(draftState).forEach(rawQueryId => {
    const queryId = removeKeysFromQueryId(['orderBy'], rawQueryId)

    Object.keys(searches).forEach(rawSearchDiffQueryId => {
      // Remove the type:widget when we implement the unified conversation index
      if (!isForCurrentUser(rawSearchDiffQueryId, currentUserId)) return
      const searchDiffQueryId = normalizeSearchQueryId(
        `${removeContextFromQueryId(rawSearchDiffQueryId)} type:widget`
      )

      if (searchDiffQueryId !== queryId) return

      const searchDiff = searches[rawSearchDiffQueryId]
      const search = draftState[rawQueryId]

      const currentSearch = debug.enabled ? deepCopy(search) : null
      const { plus, minus } = searchDiff

      let increaseCountBy = searchDiff.plus.length
      let decreaseCountBy = searchDiff.minus.length

      // All the ticket ids we have cached on all pages
      const {
        conversationIds,
        removedConversationIds,
        addedConversationIds,
        orderBy,
      } = search

      plus.forEach(conversationId => {
        const currentIndex = conversationIds.indexOf(conversationId)
        // conversation is already in the list, no need to add
        if (currentIndex >= 0) {
          // and also no need to change the search count for this conversation
          increaseCountBy -= 1
          without(addedConversationIds, conversationId)
        } else {
          // If sorting by oldest and we have *all* pages cached, then we
          // can simply add the new conversation to the end of the list. If we
          // dont have all pages cached, we cant know where to insert it,
          // so we do nothing.
          // eslint-disable-next-line no-lonely-if
          if (byOldest(orderBy)) {
            // see if we have the last page cached...
            if (search.hasAllPages) {
              withPush(conversationIds, conversationId)
              without(removedConversationIds, conversationId)
            }
          } else {
            // ASSUMES ORDERED BY NEWEST
            withUnshift(conversationIds, conversationId)
            without(removedConversationIds, conversationId)
          }
        }
      })

      minus.forEach(conversationId => {
        const currentIndex = conversationIds.indexOf(conversationId)
        if (currentIndex >= 0) {
          without(conversationIds, conversationId)
          without(addedConversationIds, conversationId)

          // ticket is present in removed, do not decrease count
        } else if (removedConversationIds.includes(conversationId)) {
          decreaseCountBy -= 1
        }
        withPush(removedConversationIds, conversationId)
      })

      search.conversationCount += increaseCountBy
      search.conversationCount -= decreaseCountBy

      if (search.conversationCount < 0) {
        if (debug.enabled) {
          // eslint-disable-next-line no-console
          console.error('got negative search count', {
            queryId,
            currentSearch,
            updatedSearch: deepCopy(draftState[queryId]),
            update: searchDiff,
          })
        }
        search.conversationCount = 0
      }
    })
  })
}

reducers[ROOM_OPTIMISTIC_UPDATED] = (draftState, action) => {
  const {
    payload: { roomId, room, oldRoom, foldersById },
  } = action

  // We dont currently support per channel folders for the rooms
  // We'll look at adding this down the line
  // const mailboxId = ticket.mailboxId
  // const mailboxesById = selectMailboxesById(state)
  // const mailbox = mailboxesById[mailboxId]
  // if (!mailbox) return result
  // if (!mailbox.folders) return result

  const searchDiffs = calculateBasicDiff(room, oldRoom, foldersById)

  Object.keys(draftState).forEach(rawQueryId => {
    const queryId = removeKeysFromQueryId(['orderBy'], rawQueryId)

    Object.keys(searchDiffs).forEach(rawSearchDiffQueryId => {
      const searchDiffQueryId = normalizeSearchQueryId(
        `${rawSearchDiffQueryId} type:widget`
      )
      if (queryId !== searchDiffQueryId) return
      const search = draftState[rawQueryId]
      const countDiff = searchDiffs[rawSearchDiffQueryId]

      if (countDiff < 0) {
        without(search.conversationIds, roomId)
        without(search.addedConversationIds, roomId)
        withPush(search.removedConversationIds, roomId)
        search.conversationCount += countDiff
      } else if (countDiff > 0 && !search.conversationIds.includes(roomId)) {
        withUnshift(search.conversationIds, roomId)
        withPush(search.addedConversationIds, roomId)
        without(search.removedConversationIds, roomId)
        search.conversationCount += countDiff
      }
    })
  })
  return draftState
}

reducers[ROOMS_UPDATED] = (draftState, action) => {
  const {
    payload: { roomUpdates, loadingMatrixIds },
  } = action
  if (!roomUpdates) return draftState

  const eventsByRoom = roomUpdates.reduce((out, { room, events }) => {
    // If we're loading this room as part of a ROOMS_FETCH, then no resorting
    // rules should be applied as the ES order is authorative
    if (loadingMatrixIds.includes(room.roomId)) return out

    const roomId = getRoomId(room)
    const roomMessageEvents = events.filter(event => {
      const roomEvents = room.getLiveTimeline().getEvents()
      const lastEvent = roomEvents[roomEvents.length - 1]

      return (
        event.getType() === 'm.room.message' &&
        (!roomEvents.includes(event) || event === lastEvent)
      )
    })
    if (roomId && roomMessageEvents.length > 0) {
      out[roomId] = roomMessageEvents
    }
    return out
  }, {})

  // Break out early if we have nothing to update
  if (Object.keys(eventsByRoom).length === 0) return draftState

  Object.keys(draftState).forEach(queryId => {
    const { conversationIds, orderBy, loaded, hasAllPages } = draftState[
      queryId
    ]
    if (loaded) {
      Object.keys(eventsByRoom).forEach(rawRoomId => {
        const roomId = parseInt(rawRoomId, 10)
        if (conversationIds.includes(roomId)) {
          if (byOldest(orderBy)) {
            // see if we have the last page cached...
            if (hasAllPages) {
              without(conversationIds, roomId)
              withPush(conversationIds, roomId)
            }
          } else {
            // ASSUMES ORDERED BY NEWEST
            without(conversationIds, roomId)
            withUnshift(conversationIds, roomId)
          }
        }
      })
    }
  })

  return draftState
}

reducers[ROOM_DELETE_STARTED] = (draftState, action) => {
  const {
    payload: { conversationId },
  } = action

  Object.keys(draftState).forEach(queryId => {
    if (draftState[queryId].conversationIds.includes(conversationId)) {
      without(draftState[queryId].conversationIds, conversationId)
      withPush(draftState[queryId].removedConversationIds, conversationId)
    }
  })
  return draftState
}

export const byQueryId = createActionTypeReducer(
  reducers,
  byQueryIdInitialState
)
